/**
 * Task status helpers shared by the badge, /tasks, Dashboard and Reports so they
 * all agree on what "open", "done" and "overdue" mean (DEV-331).
 *
 * Task statuses: open | in_progress | pending_info | resolved | closed.
 * "completed" is NOT a task status — code that compared against it treated
 * every resolved task as still open.
 */
export const TASK_DONE_STATUSES = ["resolved", "closed"] as const;

type TaskLike = { status?: string | null; dueDate?: string | Date | null };

export function isTaskDone(status: string | null | undefined): boolean {
  return status === "resolved" || status === "closed";
}

/** Not yet resolved/closed (open, in_progress, pending_info). */
export function isTaskOpen(status: string | null | undefined): boolean {
  return !isTaskDone(status);
}

/** Local calendar day as YYYY-MM-DD. */
export function toLocalDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Calendar day of a due date. dueDate is date-only in meaning; the API returns
 * it as an ISO timestamp at UTC midnight, so take the date part as written
 * rather than letting `new Date()` shift it a day back in US time zones.
 */
export function dueDateKey(due: string | Date | null | undefined): string | null {
  if (!due) return null;
  if (due instanceof Date) {
    return Number.isNaN(due.getTime()) ? null : due.toISOString().slice(0, 10);
  }
  const m = String(due).match(/^\d{4}-\d{2}-\d{2}/);
  return m ? m[0] : null;
}

/** Open and due before today (a task due today is not overdue yet). */
export function isTaskOverdue(task: TaskLike, now: Date = new Date()): boolean {
  if (isTaskDone(task.status)) return false;
  const due = dueDateKey(task.dueDate);
  return !!due && due < toLocalDateKey(now);
}
